import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api, TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};

// ---- Canonical admin resolver — the ONE source of truth for the UI.
// Backend mirror: services/authz.py. Keep the two in sync.
// SUPER_ADMIN_ONLY_KEYS shrank to just `admin.super_admin` so that
// Admins can enter System Settings + Integrations pages (destructive
// controls inside are guarded via `isSuperAdmin` at the ACTION level).
const SUPER_ADMIN_ONLY_KEYS = new Set(["admin.super_admin"]);

// Legacy role slugs that historically meant "admin tier". Kept for
// backward-compat with old JWTs / seed users that don't carry a
// `is_admin` role-class flag. The server may also set an
// `is_admin` boolean directly on the /auth/me payload — checked first.
const LEGACY_ADMIN_ROLES = new Set(["admin", "super_admin"]);

const _resolveIsSuperAdmin = (user) => user?.role === "super_admin";

const _resolveIsAdmin = (user) => {
  if (!user) return false;
  if (_resolveIsSuperAdmin(user)) return true;
  if (user.is_admin === true) return true;
  if (LEGACY_ADMIN_ROLES.has(user.role)) return true;
  return false;
};

const _resolveCan = (user, allowedPerms, permKey) => {
  if (!user || !permKey) return false;
  if (_resolveIsSuperAdmin(user)) return true;
  if (_resolveIsAdmin(user)) return !SUPER_ADMIN_ONLY_KEYS.has(permKey);
  if (!allowedPerms) return false;
  return allowedPerms.has(permKey);
};

export const AuthProvider = ({ children }) => {
  // null = checking, false = anonymous, object = authenticated
  const [user, setUser] = useState(null);
  const [allowedPerms, setAllowedPerms] = useState(null);

  const loadPermissions = async () => {
    try {
      const r = await api.get("/dashboard/permissions/me");
      const list = Array.isArray(r?.data?.allowed) ? r.data.allowed : [];
      setAllowedPerms(new Set(list));
    } catch {
      setAllowedPerms(new Set());
    }
  };

  // Wave 4B-3 P1: React StrictMode double-invokes effects in dev, which
  // was firing /auth/me and /dashboard/permissions/me twice on every mount.
  // The didInitRef guard mirrors the pattern used in PortalContext to
  // ensure the bootstrap runs exactly once per real mount.
  const didInitRef = useRef(false);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        loadPermissions();
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setUser(false);
      });
  }, []);

  // Test / cross-tab support · when the token is injected via
  // `localStorage.setItem('aa_ops_token', ...)` from an external script
  // (e.g. Playwright), or is cleared in another tab, re-bootstrap the
  // auth state instead of getting stuck on "Checking session…".
  useEffect(() => {
    const onStorage = (e) => {
      if (e && e.key && e.key !== TOKEN_KEY) return;
      const t = localStorage.getItem(TOKEN_KEY);
      if (!t) {
        setUser(false);
        setAllowedPerms(null);
        return;
      }
      api
        .get("/auth/me")
        .then((r) => { setUser(r.data); loadPermissions(); })
        .catch(() => { localStorage.removeItem(TOKEN_KEY); setUser(false); });
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Wave 4B-6 · when the tab regains focus, re-fetch permissions so an
  // admin edit made in another window (or freshly-granted capability
  // from a role class change) shows up without a manual reload.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && user && typeof user === "object") {
        loadPermissions();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [user]);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, r.data.access_token);
    setUser(r.data.user);
    loadPermissions();
    return r.data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
    setAllowedPerms(null);
  };

  // Derived helpers — always up to date with the latest user/perms.
  const helpers = useMemo(() => {
    const u = user && typeof user === "object" ? user : null;
    return {
      isAdmin: _resolveIsAdmin(u),
      isSuperAdmin: _resolveIsSuperAdmin(u),
      can: (permKey) => _resolveCan(u, allowedPerms, permKey),
    };
  }, [user, allowedPerms]);

  return (
    <AuthContext.Provider value={{ user, login, logout, setUser, allowedPerms, refreshPermissions: loadPermissions, ...helpers }}>
      {children}
    </AuthContext.Provider>
  );
};
